import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { PipeResults } from "../../shared/enums/slotted-results.enum";
import { Attachment, LoadSlottedOutputs, LoadSlottedPerforatedAttachments, Results } from "../models/slotted-pipe";

@Injectable({
  providedIn: "root",
})
export class SlottedService {
  private slottedResultsSource$: Subject<Results> = new Subject<Results>();
  slottedResults$ = this.slottedResultsSource$.asObservable();

  private showSlottedResultsSource$: Subject<boolean> = new Subject<boolean>();
  showSlottedResults$ = this.showSlottedResultsSource$.asObservable();

  private attachmentsSource$: Subject<Attachment[]> = new Subject<Attachment[]>();
  attachments$ = this.attachmentsSource$.asObservable();

  private attachmentsUploadedSource$: Subject<LoadSlottedPerforatedAttachments[]> = new Subject<LoadSlottedPerforatedAttachments[]>();
  attachmentsUploaded$ = this.attachmentsUploadedSource$.asObservable();

  private dataCheckDataSource$: Subject<any> = new Subject<any>();
  dataCheckData$ = this.dataCheckDataSource$.asObservable();

  private dataCheckFixSource$: Subject<any> = new Subject<any>();
  dataCheckFix$ = this.dataCheckFixSource$.asObservable();

  private refreshDataCheckDataSource$: Subject<any> = new Subject<any>();
  refreshDataCheckData$ = this.refreshDataCheckDataSource$.asObservable();

  private showRangeModalSource$: Subject<boolean> = new Subject<boolean>();
  showRangeModal$ = this.showRangeModalSource$.asObservable();

  private slottedInputsSource$: Subject<any> = new Subject<any>();
  slottedInputs$ = this.slottedInputsSource$.asObservable();

  private slottedInputsGangSource$: Subject<any> = new Subject<any>();
  slottedInputGang$ = this.slottedInputsGangSource$.asObservable();

  private slottedInputsGroovesSource$: Subject<boolean> = new Subject<boolean>();
  slottedInputGrooves$ = this.slottedInputsGroovesSource$.asObservable();

  private dcpProductSource$: Subject<any> = new Subject<any>();
  dcpProduct$ = this.dcpProductSource$.asObservable();

  private closeDcpModalSource$: Subject<boolean> = new Subject<boolean>();
  closeDcpModal$ = this.closeDcpModalSource$.asObservable();

  private submitSource$: Subject<void> = new Subject<void>();
  submit$ = this.submitSource$.asObservable();

  constructor() {}

  setSlottedInputsDisable(data: any){
    this.slottedInputsSource$.next(data);
  }

  setSlottedInputsGang(data: any){
    this.slottedInputsGangSource$.next(data);
  }

  setSlottedInputGrooves(grooves: boolean){
    this.slottedInputsGroovesSource$.next(grooves);
  }

  setCloseDcpModal(closeDcpModal: boolean) {
    this.closeDcpModalSource$.next(closeDcpModal);
  }

  setDcpProduct(dcpProduct: any) {
    this.dcpProductSource$.next(dcpProduct);
  }

  setRefreshDataCheckData(section: string, isSubmitting: boolean = false) {
    this.refreshDataCheckDataSource$.next({section: section, isSubmitting: isSubmitting});
  }

  setDataCheckFix(data: any, section: string, isSubmitting: boolean = false) {
    this.dataCheckFixSource$.next({data: data, section: section, isSubmitting: isSubmitting});
  }

  setSlottedResults(slottedResults: Results) {
    this.slottedResultsSource$.next(slottedResults);
  }

  setSlottedResultsOnLoad(slottedOutputs: LoadSlottedOutputs) {
    let slottedResults: Results = {
      openAreaInchAtID: slottedOutputs.open_area_inch_at_id,
      openAreaInchAtOD: slottedOutputs.open_area_inch_at_od,
      openAreaPercentAtID: slottedOutputs.open_area_percent_at_id,
      openAreaPercentAtOD: slottedOutputs.open_area_percent_at_od,
      results: {
        [PipeResults.Axial]: {
          pipeBodyRating: null,
          slottedPipeRating: null,
          reductionFactor: null
        },
        [PipeResults.Bending]: {
          pipeBodyRating: null,
          slottedPipeRating: null,
          reductionFactor: null
        },
        [PipeResults.Collapse]: {
          pipeBodyRating: null,
          slottedPipeRating: null,
          reductionFactor: null
        },
        [PipeResults.Torsion]: {
          pipeBodyRating: null,
          slottedPipeRating: null,
          reductionFactor: null
        },
      },
    };

    slottedOutputs.results.forEach((loadMode) => {
      switch (loadMode.key) {
        case PipeResults.Axial:
          slottedResults.results.Axial.pipeBodyRating =
            loadMode.pipe_body_rating;
          slottedResults.results.Axial.slottedPipeRating =
            loadMode.slotted_pipe_rating;
          slottedResults.results.Axial.reductionFactor =
            loadMode.reduction_factor;
        case PipeResults.Bending:
          slottedResults.results.Bending.pipeBodyRating =
            loadMode.pipe_body_rating;
          slottedResults.results.Bending.slottedPipeRating =
            loadMode.slotted_pipe_rating;
          slottedResults.results.Bending.reductionFactor =
            loadMode.reduction_factor;
        case PipeResults.Collapse:
          slottedResults.results.Collapse.pipeBodyRating =
            loadMode.pipe_body_rating;
          slottedResults.results.Collapse.slottedPipeRating =
            loadMode.slotted_pipe_rating;
          slottedResults.results.Collapse.reductionFactor =
            loadMode.reduction_factor;
        case PipeResults.Torsion:
          slottedResults.results.Torsion.pipeBodyRating =
            loadMode.pipe_body_rating;
          slottedResults.results.Torsion.slottedPipeRating =
            loadMode.slotted_pipe_rating;
          slottedResults.results.Torsion.reductionFactor =
            loadMode.reduction_factor;
      }
    });

    this.slottedResultsSource$.next(slottedResults);
  }

  setShowSlottedResults(showSlottedResults: boolean) {
    this.showSlottedResultsSource$.next(showSlottedResults);

    if (!showSlottedResults) {
      this.setSlottedResults(null);
    }
  }

  setAttachments(attachments: Attachment[]) {
    this.attachmentsSource$.next(attachments);
  }

  setAttachmentsUploaded(attachments: LoadSlottedPerforatedAttachments[]) {
    this.attachmentsUploadedSource$.next(attachments);
  }

  setDataCheckData(dataCheckData: any, section: string, isSubmitting: boolean = false, anySectionWithErrors: boolean = false, anySectionWithWarnings: boolean = false) {
    this.dataCheckDataSource$.next({data: dataCheckData, section: section, isSubmitting: isSubmitting, anySectionWithErrors: anySectionWithErrors, anySectionWithWarnings: anySectionWithWarnings});
  }

  setShowRangeModal(show: boolean) {
    this.showRangeModalSource$.next(show);
  }

  submit() {
    this.submitSource$.next();
  }
}
